import { createSelector } from 'reselect';
import _ from 'lodash';

import { localPreferences } from 'companion-app-components/utils/core';
import { authSelectors } from 'companion-app-components/flux/auth';
import { entitlementsSelectors } from 'companion-app-components/flux/entitlements';
import { accountGroupsSelectors } from 'companion-app-components/flux/account-groups';
import { datasetsSelectors } from 'companion-app-components/flux/datasets';
import { hasCFOEntitlementForDataset } from 'companion-app-components/flux/entitlements/entitlementsUtils';

import { getActiveSubscription } from 'data/subscriptions/subscriptionsSelectors';
import { getSubscriptionTrackingProps } from 'data/subscriptions/helpers';
import { createValueCache } from 'utils/selectorsHelpers';
import { APP_STRING_CLIENT_TYPE } from 'utils/constants';

export const getMixpanelUserData = createSelector(
  authSelectors.getAuthSession,
  getActiveSubscription,
  createValueCache((authSession, subscription) => ({
    qcs_id: authSession.qcsId ?? 'unknown',
    ...(authSession.datasetId && { dataset_id: authSession.datasetId }),
    ...(authSession.scope && { scope: authSession.scope }),
    ...(authSession.groups && { groups: authSession.groups }),
    ...(subscription?.active !== undefined && { subscriber: subscription.active ? 'Subscribed' : 'Not Subscribed' }),
    ...(subscription?.productLineName && { product_line: subscription.productLineName }),
    ...(subscription?.tierName && { tier: subscription.tierName }),
    ...(subscription?.isInTrialPeriod != null && { subscription_state: subscription.isInTrialPeriod ? 'trial' : 'paid' }),
    ...(subscription?.renewalFrequency != null && {
      subscription_term: subscription.renewalFrequency === 'P1M' ? 'monthly' : 'annual',
    }),
    last_client: APP_STRING_CLIENT_TYPE,
    simplifi_web_app_version: process.env.APP_VERSION,
  })),
);

export const getMixpanelDefaultProperties = createSelector(
  authSelectors.getAuthSession,
  getActiveSubscription,
  entitlementsSelectors.getEntitlements,
  authSelectors.getDatasetId,
  authSelectors.getCurrentDataset,
  datasetsSelectors.getDatasetsById,
  accountGroupsSelectors.getAccountGroups,
  accountGroupsSelectors.selectSharedAccountGroup,
  createValueCache((authSession, subscription, entitlements, datasetId, currentDataset, datasetsById, allEntities, sharedAccountGroup) => {
    const defaultProps = {};

    defaultProps.qcs_id = authSession?.qcsId ?? 'unknown';
    defaultProps.dataset_id = authSession?.datasetId;
    defaultProps.dataset_platform = authSession?.datasetPlatform;
    defaultProps.dataset_created_by = authSession?.datasetCreatedByClientId;
    defaultProps.scope = authSession?.scope;
    defaultProps.groups = authSession?.groups;

    const subscriptionProps = getSubscriptionTrackingProps(subscription);
    Object.keys(subscriptionProps).forEach((propName) => {
      defaultProps[_.snakeCase(propName)] = subscriptionProps[propName];
    });

    defaultProps.entitlements = entitlements && entitlements
      .filter((entitlement) => entitlement.active)
      .map((entitlement) => entitlement.id)
      .toIndexedSeq()
      .toArray();

    defaultProps.persist = localPreferences.getPersist();
    defaultProps.keep_logged_in = localPreferences.getKeepLoggedIn();

    const datasetEntitlements = datasetsById?.get(datasetId)?.entitlements;
    const isCFOUser = hasCFOEntitlementForDataset(datasetEntitlements);
    const isSharedDataset = Boolean(currentDataset?.shared);

    if (isCFOUser) {
      const entities = isSharedDataset ? sharedAccountGroup : allEntities;
      defaultProps.entity_count = entities?.length ?? 0;
      defaultProps.user_type = isSharedDataset ? 'Shared User' : 'Owner';
    }

    return defaultProps;
  }),
);
